import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, Shield, Zap, Users, Activity,
  Stethoscope, Brain, Clock, ArrowRight, CheckCircle2,
  Menu, X, AlertTriangle, Pill, Phone, Home, TrendingUp,
  Bell, Smile, Meh, AlertOctagon, Check, Calendar,
  BarChart3, Sparkles, UserCheck
} from 'lucide-react';
import '../styles/design-system.css';

// ── Data ─────────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: Heart,
    title: '2-tap patient reporting',
    description: 'A mood check-in and plain-language issue tiles — no medical jargon, no forms. Works for elderly and low-literacy patients.',
    accent: '#97f2f3', iconColor: '#007072',
  },
  {
    icon: Brain,
    title: 'Clinical risk engine',
    description: 'Five clinical rules — CHF early warning, COPD exacerbation, pneumonia, medication cascade, care breakdown — run on every patient signal.',
    accent: '#d6e3ff', iconColor: '#00478d',
  },
  {
    icon: AlertTriangle,
    title: 'Smart post-discharge outreach',
    description: 'Automatic check-in nudges at Day 1 (×3), Day 3, Day 7, and Day 14 after discharge — idempotent, no duplicates, no manual scheduling.',
    accent: '#ffdea8', iconColor: '#5f4300',
  },
  {
    icon: Zap,
    title: 'Priority action queue',
    description: 'Clinicians see action cards — not alerts. One-sentence reason, one recommended action, one Execute button. Under 30 seconds per decision.',
    accent: '#d6e3ff', iconColor: '#00478d',
  },
  {
    icon: Sparkles,
    title: 'AI clinical summaries',
    description: 'Every patient card shows a Groq-powered 2-sentence clinical summary and a 14-point risk trend sparkline — updated on every assessment.',
    accent: '#e8d5ff', iconColor: '#6d28d9',
  },
  {
    icon: BarChart3,
    title: 'Population health grid',
    description: 'A doctor\'s full patient panel sorted by risk score, discharge window, or silence. AI panel insight in 2 sentences at the top.',
    accent: '#97f2f3', iconColor: '#007072',
  },
  {
    icon: Calendar,
    title: 'Care plan templates',
    description: 'CHF, COPD, orthopedic, stroke, pneumonia — one click pre-fills the follow-up schedule. Calendar conflict detection built in.',
    accent: '#ffdea8', iconColor: '#5f4300',
  },
  {
    icon: Users,
    title: 'Full caregiver integration',
    description: 'Caregivers receive real-time alerts for high-severity symptoms, appointments, and risk escalations. Two-way messaging and a symptom feed — all behind patient-controlled consent.',
    accent: '#d6e3ff', iconColor: '#00478d',
  },
  {
    icon: Pill,
    title: 'Medication adherence tracking',
    description: 'Patients log each dose with one tap. Adherence rate computed over rolling 30 days. Doctors see the rate on the patient profile — before prescribing changes.',
    accent: '#ffdea8', iconColor: '#5f4300',
  },
  {
    icon: Phone,
    title: 'Emergency SOS',
    description: 'One-tap emergency alert that notifies the patient\'s doctor and all active caregivers simultaneously via push, email, and SMS — with no deduplication delay.',
    accent: '#fde8e8', iconColor: '#991b1b',
  },
  {
    icon: Shield,
    title: 'PHI encryption + consent controls',
    description: 'Clinical notes, conditions, and medications are encrypted at the field level (AES-256-GCM) before reaching the database. Patients also independently control which data categories each doctor can access — revocation is immediate and enforced at the API layer.',
    accent: '#97f2f3', iconColor: '#007072',
  },
];

const HOW_STEPS = [
  {
    num: '01',
    icon: Heart,
    who: 'Patient',
    whoColor: '#97f2f3', whoText: '#007072',
    title: 'Reports in 2 taps',
    body: 'Alice taps "Not Great" — then "I don\'t feel well." Optionally adds one sentence. That\'s it. No login friction, no forms, no medical jargon.',
    detail: 'Works for elderly, low-literacy, and low-engagement patients.',
  },
  {
    num: '02',
    icon: Brain,
    who: 'Intelligence',
    whoColor: '#d6e3ff', whoText: '#00478d',
    title: 'Detects the pattern',
    body: 'The risk engine matches Alice\'s signal — swelling, shortness of breath, 3 days post-CHF discharge — against clinical rules. Risk score: 78. Action: urgent clinical review.',
    detail: 'No black box. Every factor is labeled and explainable.',
  },
  {
    num: '03',
    icon: Zap,
    who: 'Clinician',
    whoColor: '#ffdea8', whoText: '#5f4300',
    title: 'Acts in under 30 seconds',
    body: 'Dr. Chen sees a card: "CHF patient, 3 days post-discharge, swelling reported, no caregiver." AI summary pre-loaded. Recommended action: urgent home visit. One button.',
    detail: 'Not a list of alerts. A ranked, actionable queue with AI context.',
  },
  {
    num: '04',
    icon: Bell,
    who: 'Patient',
    whoColor: '#97f2f3', whoText: '#007072',
    title: 'Loop closed — everyone notified',
    body: 'Alice\'s phone: "A nurse will visit within 24 hours." Her daughter\'s caregiver portal updates: "Under monitoring." No one wonders what happens next.',
    detail: 'Real-time WebSocket push + email confirmation.',
  },
];

const STATS = [
  { value: '30%', label: 'of high-risk patients readmitted within 30 days', source: 'HHS' },
  { value: '3.8M', label: 'preventable readmissions per year in the US', source: 'HHS' },
  { value: '< 30s', label: 'average clinical decision time on our action queue', source: 'Product' },
  { value: '12+', label: 'platform capabilities shipped — AI, caregiver, SOS, adherence, notes', source: 'Product' },
];

const PERSONAS = [
  {
    role: 'Patient',
    color: '#007072', bg: '#97f2f3', lightBg: '#f0fffe',
    icon: Heart,
    headline: 'Never left wondering what happens next',
    points: [
      '2-tap mood check-in — no forms, no jargon',
      'One-tap medication adherence log with 30-day rate',
      'Emergency SOS — instantly alerts doctor and caregivers',
      'Automatic check-in nudges after discharge',
      'Email when an appointment is scheduled',
    ],
  },
  {
    role: 'Clinician',
    color: '#00478d', bg: '#d6e3ff', lightBg: '#f3f6ff',
    icon: Stethoscope,
    headline: 'Full clinical picture. One screen.',
    points: [
      'Priority action queue — ranked cards, not alert lists',
      'AI summary + 14-point risk trend sparkline per patient',
      'Population grid sorted by risk, discharge window, silence',
      'Private clinical notes per patient — never visible to patients',
      'Weekly panel digest email every Monday morning',
    ],
  },
  {
    role: 'Family',
    color: '#5f4300', bg: '#ffdea8', lightBg: '#fffbf0',
    icon: Users,
    headline: 'Stay informed without the clinical details',
    points: [
      'Real-time alerts: symptoms, appointments, risk escalations',
      'Symptom feed with severity colour coding',
      'Medication schedule visibility',
      'Two-way messaging with the patient',
      'Revocable anytime by the patient',
    ],
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const Landing = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setMobileMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const navLinks = [
    { href: '#problem', label: 'The Problem' },
    { href: '#how', label: 'How it works' },
    { href: '#features', label: 'Features' },
    { to: '/our-goal', label: 'Our Goal' },
    { to: '/how-it-works', label: 'Deep dive →' },
  ];

  return (
    <div className="min-h-screen font-body" style={{ background: '#f9f9fd', color: '#191c1e' }}>

      {/* ── Nav ── */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled || mobileMenuOpen ? 'bg-[#f9f9fd]/95 backdrop-blur-xl shadow-[0px_4px_16px_rgba(25,28,30,0.06)]' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
              <Stethoscope className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
            </div>
            <span className="font-headline font-bold text-base sm:text-lg tracking-tight" style={{ color: '#191c1e' }}>
              Sympto<span style={{ color: '#00478d' }}>Connect</span>
            </span>
          </div>

          <div className="hidden md:flex items-center gap-6 lg:gap-8 text-sm font-semibold" style={{ color: '#424752' }}>
            {navLinks.map(l =>
              l.to
                ? <Link key={l.to} to={l.to} className="transition-colors hover:text-[#00478d]">{l.label}</Link>
                : <a key={l.href} href={l.href} className="transition-colors hover:text-[#00478d]">{l.label}</a>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" className="hidden sm:block text-sm font-bold px-3 py-2 transition-colors hover:text-[#005eb8]" style={{ color: '#00478d' }}>Sign In</Link>
            <Link
              to="/register"
              className="text-xs sm:text-sm font-bold text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-full min-h-[36px] sm:min-h-[44px] flex items-center transition-all hover:shadow-[0_4px_16px_rgba(0,71,141,0.3)]"
              style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}
            >
              Get Started
            </Link>
            <button
              className="md:hidden p-2 rounded-lg hover:bg-black/5 transition-colors"
              onClick={() => setMobileMenuOpen(v => !v)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" style={{ color: '#191c1e' }} /> : <Menu className="w-5 h-5" style={{ color: '#191c1e' }} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-[#f9f9fd]/98 backdrop-blur-xl px-4 py-4 space-y-1" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
            {navLinks.map(l =>
              l.to
                ? <Link key={l.to} to={l.to} onClick={() => setMobileMenuOpen(false)} className="flex items-center h-11 px-3 rounded-xl text-sm font-semibold transition-all hover:bg-black/5" style={{ color: '#00478d' }}>{l.label}</Link>
                : <a key={l.href} href={l.href} onClick={() => setMobileMenuOpen(false)} className="flex items-center h-11 px-3 rounded-xl text-sm font-semibold transition-all hover:bg-black/5 hover:text-[#00478d]" style={{ color: '#424752' }}>{l.label}</a>
            )}
            <div className="pt-2 mt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex items-center h-11 px-3 rounded-xl text-sm font-bold transition-all hover:bg-black/5" style={{ color: '#00478d' }}>Sign In</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section className="relative pt-20 pb-14 sm:pt-28 sm:pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[120px] pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(0,71,141,0.08),transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-[100px] pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(0,105,107,0.06),transparent 70%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6" style={{ background: '#e8d5ff', color: '#6d28d9' }}>
              <Sparkles className="w-3 h-3" />
              AI-powered post-discharge care
            </div>

            <h1 className="font-headline font-extrabold leading-[1.08] mb-5" style={{ fontSize: 'clamp(2.1rem,5vw,3.6rem)', letterSpacing: '-0.025em', color: '#191c1e' }}>
              The care layer between{' '}
              <span style={{ background: 'linear-gradient(135deg,#00478d,#00696b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                hospital and home.
              </span>
            </h1>

            <p className="text-base sm:text-lg leading-relaxed mb-8 max-w-lg" style={{ color: '#424752' }}>
              When patients leave the hospital, clinicians lose visibility. SymptoConnect fills that gap — surfacing early deterioration signals, routing care teams to act, and keeping families informed.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Link
                to="/how-it-works"
                className="group inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-full font-bold text-white text-sm min-h-[48px] transition-all hover:shadow-[0_8px_24px_rgba(0,71,141,0.3)]"
                style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}
              >
                See how it works
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-full font-bold text-sm min-h-[48px] transition-all hover:bg-[#e7e8eb]"
                style={{ background: '#f3f3f7', color: '#191c1e' }}
              >
                Start for free
              </Link>
            </div>

            <div className="flex flex-wrap gap-4 text-sm font-medium" style={{ color: '#424752' }}>
              {[
                'PHI encrypted at rest · AES-256-GCM',
                'Emergency SOS built-in',
                'Caregiver portal + alerts',
              ].map(t => (
                <span key={t} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#00696b' }} />
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Right — dual mock cards */}
          <div className="relative mt-4 lg:mt-0 flex flex-col gap-4">
            {/* Patient card */}
            <div className="rounded-2xl p-4 sm:p-5" style={{ background: '#ffffff', boxShadow: '0px 8px 24px rgba(25,28,30,0.08)' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: '#00696b' }}>A</div>
                <div>
                  <p className="text-xs font-bold" style={{ color: '#191c1e' }}>Alice Johnson, 75</p>
                  <p className="text-[10px]" style={{ color: '#727783' }}>CHF · 3 days post-discharge · Lives alone</p>
                </div>
                <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#ffdea8', color: '#5f4300' }}>High Risk</span>
              </div>
              <p className="text-xs font-semibold mb-2.5" style={{ color: '#727783' }}>How are you feeling right now?</p>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { Icon: Smile, label: 'Good', bg: '#f0fdf4', c: '#166534', activeBorder: '#86efac', active: false },
                  { Icon: Meh, label: 'Not Great', bg: '#fefce8', c: '#854d0e', activeBorder: '#fbbf24', active: true },
                  { Icon: AlertOctagon, label: 'Need Help', bg: '#fef2f2', c: '#991b1b', activeBorder: '#fca5a5', active: false },
                ].map(({ Icon, label, bg, c, activeBorder, active }) => (
                  <div key={label} className="flex flex-col items-center py-3 rounded-xl border-2" style={{ background: active ? bg : '#f9f9fd', borderColor: active ? activeBorder : 'transparent' }}>
                    <Icon className="w-6 h-6 mb-1.5" style={{ color: active ? c : '#9ca3af' }} />
                    <span className="text-[10px] font-bold" style={{ color: active ? c : '#727783' }}>{label}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-xl p-3 text-xs" style={{ background: '#f3f3f7', color: '#424752' }}>
                <span className="font-semibold" style={{ color: '#191c1e' }}>my ankles are swollen again</span> and i cant catch my breath walking to the bathroom
              </div>
            </div>

            {/* Doctor action card — now with AI summary */}
            <div className="rounded-2xl p-4 sm:p-5" style={{ background: '#ffffff', boxShadow: '0px 8px 24px rgba(25,28,30,0.08)' }}>
              <div className="flex items-center justify-between mb-3">
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' }}>CRITICAL · Score 78</span>
                <span className="text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1" style={{ background: '#fef9c3', color: '#854d0e', border: '1px solid #fde68a' }}>
                  <Clock className="w-2.5 h-2.5" /> 3h 42m remaining
                </span>
              </div>
              <p className="font-bold text-sm mb-2" style={{ color: '#191c1e' }}>Alice Johnson</p>

              {/* AI summary row */}
              <div className="rounded-lg p-2.5 mb-3 flex items-start gap-2" style={{ background: '#f3f0ff', border: '1px solid #e8d5ff' }}>
                <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#6d28d9' }} />
                <p className="text-[10px] leading-relaxed" style={{ color: '#4c1d95' }}>Alice presents with progressive dyspnea and bilateral ankle edema 3 days post-CHF discharge with a missed diuretic pickup — pattern consistent with early decompensation.</p>
              </div>

              <div className="rounded-lg border overflow-hidden mb-3" style={{ borderColor: '#e2e8f0' }}>
                <div className="px-3 py-2 flex flex-wrap gap-1" style={{ background: '#f8fafc' }}>
                  {['CHF', 'Hypertension', 'A-Fib', 'No caregiver'].map(c => (
                    <span key={c} className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#f1f5f9', color: '#475569' }}>{c}</span>
                  ))}
                </div>
              </div>
              <button className="w-full py-2.5 rounded-xl text-xs font-bold text-white transition-all flex items-center justify-center gap-1.5" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
                <Check className="w-3 h-3" /> Execute — Order urgent home visit
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Problem ── */}
      <section id="problem" className="py-14 sm:py-20" style={{ background: '#191c1e' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#97f2f3' }}>The Problem</p>
            <h2 className="font-headline font-extrabold text-white mb-4 px-2" style={{ fontSize: 'clamp(1.5rem,3vw,2.5rem)', letterSpacing: '-0.02em' }}>
              The 30-day window where care goes dark
            </h2>
            <p className="text-base sm:text-lg max-w-2xl mx-auto px-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
              When a high-risk patient is discharged, the clinical team's visibility drops to near zero. The signal is almost always there days before an acute event — it's just invisible to everyone.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12">
            {STATS.map((s, i) => (
              <div key={i} className="rounded-2xl p-5 sm:p-6 text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="font-headline font-extrabold mb-2" style={{ fontSize: 'clamp(1.75rem,4vw,2.5rem)', letterSpacing: '-0.03em', color: s.source === 'Product' ? '#97f2f3' : '#ffffff' }}>{s.value}</p>
                <p className="text-xs leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>{s.label}</p>
                <p className="text-[10px] font-bold uppercase tracking-wider mt-2" style={{ color: s.source === 'Product' ? '#97f2f3' : 'rgba(255,255,255,0.3)' }}>{s.source}</p>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: Pill, title: 'Medication not picked up', body: 'Pharmacy fills aren\'t connected to the care team. The patient runs out of their diuretic. No one knows.' },
              { icon: Home, title: 'Home service not delivered', body: 'The home nurse didn\'t show up. The patient waited all day. There\'s no incident report, no follow-up.' },
              { icon: Phone, title: 'Patient confused, doesn\'t call', body: 'The discharge papers are confusing. The patient doesn\'t want to be a burden. They wait until it\'s too late.' },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(151,242,243,0.1)' }}>
                  <Icon className="w-4.5 h-4.5" style={{ color: '#97f2f3' }} />
                </div>
                <p className="font-bold text-sm mb-2 text-white">{title}</p>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="how" className="py-14 sm:py-20 lg:py-24" style={{ background: '#f3f3f7' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#00478d' }}>How it works</p>
            <h2 className="font-headline font-extrabold mb-4 px-2" style={{ fontSize: 'clamp(1.5rem,3vw,2.5rem)', letterSpacing: '-0.02em', color: '#191c1e' }}>
              From tap to action in under 30 seconds
            </h2>
            <p className="text-base sm:text-lg max-w-xl mx-auto px-2" style={{ color: '#424752' }}>
              The full care loop — patient signals, AI risk detection, clinical decision, patient and family notification — completely closed.
            </p>
          </div>

          <div className="relative mb-4">
            <div
              className="hidden lg:block absolute inset-x-0 pointer-events-none"
              style={{ top: '50%', height: '3px', background: '#dde0e5', transform: 'translateY(-50%)', zIndex: 0 }}
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" style={{ position: 'relative', zIndex: 1 }}>
              {HOW_STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-full px-3 py-2.5"
                    style={{ border: `3px solid ${step.whoColor}`, background: '#ffffff', boxShadow: '0 4px 18px rgba(25,28,30,0.08)' }}
                  >
                    <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: '#f9f9fd', boxShadow: '0 2px 10px rgba(25,28,30,0.12)' }}>
                      <Icon className="w-5 h-5" style={{ color: step.whoText }} />
                    </div>
                    <div className="min-w-0">
                      <span className="inline-block text-[9px] font-bold uppercase tracking-widest mb-0.5" style={{ color: step.whoText }}>{step.who}</span>
                      <p className="text-xs font-bold leading-tight" style={{ color: '#191c1e' }}>{step.title}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-10">
            {HOW_STEPS.map((step, i) => (
              <div key={i}>
                <div className="flex items-center gap-1 mb-3 mt-1">
                  {Array.from({ length: i + 1 }).map((_, j) => (
                    <div key={j} className="w-2 h-2 rounded-full" style={{ background: step.whoText }} />
                  ))}
                </div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: step.whoText }}>{step.who}</p>
                <p className="text-xs sm:text-sm leading-relaxed mb-2" style={{ color: '#424752' }}>{step.body}</p>
                <p className="text-[10px] font-semibold" style={{ color: '#727783' }}>{step.detail}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all hover:shadow-md"
              style={{ background: '#191c1e', color: '#ffffff' }}
            >
              See the full walkthrough
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── For everyone in the care circle ── */}
      <section className="py-14 sm:py-20 lg:py-24" style={{ background: '#ffffff' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#00478d' }}>For everyone involved</p>
            <h2 className="font-headline font-extrabold mb-4 px-2" style={{ fontSize: 'clamp(1.5rem,3vw,2.5rem)', letterSpacing: '-0.02em', color: '#191c1e' }}>
              One platform. Three perspectives.
            </h2>
            <p className="text-base sm:text-lg max-w-2xl mx-auto px-2" style={{ color: '#424752' }}>
              Patients, clinicians, and families each get exactly what they need — and nothing they shouldn't see.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {PERSONAS.map(({ role, color, bg, lightBg, icon: Icon, headline, points }) => (
              <div
                key={role}
                className="rounded-3xl p-6 sm:p-8 flex flex-col"
                style={{ background: lightBg, border: `1.5px solid ${bg}` }}
              >
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 flex-shrink-0" style={{ background: bg }}>
                  <Icon className="w-6 h-6" style={{ color }} />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color }}>For the {role}</p>
                <h3 className="font-headline font-bold text-lg mb-5 leading-snug" style={{ color: '#191c1e' }}>{headline}</h3>
                <ul className="space-y-2.5 mt-auto">
                  {points.map(pt => (
                    <li key={pt} className="flex items-start gap-2.5 text-sm" style={{ color: '#424752' }}>
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color }} />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-14 sm:py-20 lg:py-24" style={{ background: '#f9f9fd' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#00478d' }}>Platform</p>
            <h2 className="font-headline font-extrabold mb-4 px-2" style={{ fontSize: 'clamp(1.5rem,3vw,2.5rem)', letterSpacing: '-0.02em', color: '#191c1e' }}>
              Built for patients who won't engage,<br className="hidden sm:block" /> clinicians who can't afford alert fatigue
            </h2>
            <p className="text-base sm:text-lg max-w-2xl mx-auto px-2" style={{ color: '#424752' }}>
              Every design decision was made with one constraint: it has to work for an 83-year-old who lives alone and doesn't want to be a burden.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className="rounded-2xl sm:rounded-3xl p-5 sm:p-7 transition-all duration-300 hover:-translate-y-1"
                  style={{ background: '#ffffff', boxShadow: '0px 4px 16px rgba(25,28,30,0.04)' }}
                >
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 flex-shrink-0" style={{ background: f.accent }}>
                    <Icon className="w-5 h-5" style={{ color: f.iconColor }} />
                  </div>
                  <h3 className="font-headline font-bold text-sm sm:text-base mb-2" style={{ color: '#191c1e' }}>{f.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#424752' }}>{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-14 sm:py-20 lg:py-24 relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#00478d 0%,#005eb8 60%,#007072 100%)' }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 70% 50%,rgba(151,242,243,0.12),transparent 60%)' }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-headline font-extrabold text-white mb-5 px-2" style={{ fontSize: 'clamp(1.75rem,4vw,3rem)', letterSpacing: '-0.02em' }}>
            Close the gap between hospital discharge and safe recovery.
          </h2>
          <p className="text-base sm:text-lg mb-10 px-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
            SymptoConnect gives care teams real-time visibility into their highest-risk patients — and gives families peace of mind between appointments.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link
              to="/how-it-works"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full font-bold text-sm min-h-[48px] transition-all hover:shadow-xl"
              style={{ background: '#ffffff', color: '#00478d' }}
            >
              <Activity className="w-4 h-4" />
              See the demo walkthrough
            </Link>
            <Link
              to="/register"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full font-bold text-white text-sm min-h-[48px] transition-all"
              style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)' }}
            >
              Start for free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-10 sm:py-16" style={{ background: '#191c1e', color: 'rgba(255,255,255,0.5)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div className="sm:col-span-2">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
                  <Stethoscope className="w-4 h-4 text-white" />
                </div>
                <span className="font-headline font-bold text-lg text-white">Sympto<span style={{ color: '#97f2f3' }}>Connect</span></span>
              </div>
              <p className="text-sm leading-relaxed max-w-xs">Continuous care intelligence for post-discharge patients — built so no one falls through the gaps.</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-white mb-4">Platform</p>
              <ul className="space-y-2.5 text-sm">
                <li><a href="/#problem" className="hover:text-white transition-colors">The Problem</a></li>
                <li><a href="/#how" className="hover:text-white transition-colors">How it works</a></li>
                <li><Link to="/how-it-works" className="hover:text-white transition-colors">Full walkthrough</Link></li>
                <li><a href="/#features" className="hover:text-white transition-colors">Features</a></li>
                <li><Link to="/our-goal" className="hover:text-white transition-colors">Our Goal</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-white mb-4">Legal</p>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p>© 2026 SymptoConnect. All rights reserved.</p>
            <p style={{ color: 'rgba(255,255,255,0.3)' }}>PHI field-encrypted (AES-256-GCM) · HIPAA-conscious architecture · No real patient data in demo</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
